import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1) {
    return null; // Don't render pagination if there's only one page
  }
  
  const getPaginationGroup = () => {
    const pages: (number | string)[] = [];
    const delta = 1; // Number of pages to show around the current page
    const left = currentPage - delta;
    const right = currentPage + delta;
    const range = [];
    let last;

    for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || (i >= left && i <= right)) {
            range.push(i);
        }
    }

    for (const i of range) {
        if (last) {
            if (i - last === 2) {
                pages.push(last + 1);
            } else if (i - last !== 1) {
                pages.push('...');
            }
        }
        pages.push(i);
        last = i;
    }

    return pages;
  };

  const pages = getPaginationGroup();

  const PageButton: React.FC<{ page: number | string, isCurrent: boolean }> = ({ page, isCurrent }) => {
    const isEllipsis = page === '...';
    
    const baseClasses = "flex items-center justify-center h-9 w-9 text-sm font-medium rounded-md transition-colors";
    const activeClasses = "bg-brand-purple text-white shadow-sm";
    const inactiveClasses = "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50";
    const ellipsisClasses = "text-gray-500";

    if (isEllipsis) {
      return <span className={`${baseClasses} ${ellipsisClasses}`}>{page}</span>;
    }
    
    return (
      <button
        onClick={() => onPageChange(page as number)}
        className={`${baseClasses} ${isCurrent ? activeClasses : inactiveClasses}`}
        aria-current={isCurrent ? 'page' : undefined}
      >
        {page}
      </button>
    );
  };

  return (
    <nav aria-label="Pagination">
      <ul className="flex items-center space-x-2">
        <li>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex items-center justify-center h-9 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
        </li>
        {pages.map((page, index) => (
          <li key={index}>
            <PageButton page={page} isCurrent={page === currentPage} />
          </li>
        ))}
        <li>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex items-center justify-center h-9 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;